import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import {
  Alert,
  Box,
  Chip,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useTournamentDraws } from "../hooks/useTournamentDraws";
import { useTournamentPlayers } from "../hooks/useTournamentPlayers";

const statusLabel = (status: string) =>
  status === "pending_review"
    ? "Pending review"
    : status === "waitlisted"
      ? "Waitlisted"
      : status.charAt(0).toUpperCase() + status.slice(1).replaceAll("_", " ");

export function TournamentPlayersSection({
  tournamentId,
}: {
  tournamentId: string;
}) {
  const players = useTournamentPlayers(tournamentId);
  const draws = useTournamentDraws(tournamentId);
  const [query, setQuery] = useState("");
  const drawNames = useMemo(
    () => new Map((draws.data ?? []).map((draw) => [draw.draw_id, draw.name])),
    [draws.data],
  );
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const filtered = useMemo(
    () =>
      (players.data ?? []).filter((entry) => {
        if (!normalizedQuery) return true;
        const haystack = [
          entry.player_display_name,
          entry.partner_display_name,
          drawNames.get(entry.draw_id),
        ]
          .filter(Boolean)
          .join(" ")
          .toLocaleLowerCase();
        return haystack.includes(normalizedQuery);
      }),
    [drawNames, normalizedQuery, players.data],
  );
  const groups = useMemo(() => {
    const byDraw = new Map<string, typeof filtered>();
    for (const entry of filtered) {
      const rows = byDraw.get(entry.draw_id) ?? [];
      rows.push(entry);
      byDraw.set(entry.draw_id, rows);
    }
    return Array.from(byDraw.entries());
  }, [filtered]);

  if (players.isLoading || draws.isLoading) {
    return <Typography role="status">Loading entrants…</Typography>;
  }
  if (players.isError || draws.isError) {
    return (
      <Alert severity="error">
        We couldn&apos;t load the entrant list. Please try again.
      </Alert>
    );
  }

  return (
    <Stack component="section" spacing={2.5} aria-label="Entrants">
      <Box>
        <Typography component="h2" variant="h5" sx={{ fontWeight: 800 }}>
          Entrants
        </Typography>
        <Typography color="text.secondary" variant="body2">
          Confirmed and pending entries by draw. Private player details are
          never shown.
        </Typography>
      </Box>
      <TextField
        fullWidth
        label="Find a player, pair, or draw"
        onChange={(event) => setQuery(event.target.value)}
        value={query}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />
      {groups.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
          <Typography color="text.secondary">
            {normalizedQuery
              ? "No entrants match your search."
              : "Entrants will appear after registration."}
          </Typography>
        </Paper>
      ) : (
        groups.map(([drawId, entries]) => (
          <Paper key={drawId} variant="outlined" sx={{ overflow: "hidden" }}>
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                bgcolor: "action.hover",
                justifyContent: "space-between",
                px: 2,
                py: 1.5,
              }}
            >
              <Typography component="h3" sx={{ fontWeight: 800 }}>
                {drawNames.get(drawId) ?? "Tournament draw"}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {entries.length} {entries.length === 1 ? "entry" : "entries"}
              </Typography>
            </Stack>
            <Stack
              divider={<Box sx={{ borderTop: 1, borderColor: "divider" }} />}
            >
              {entries.map((entry) => (
                <Stack
                  key={entry.entry_id}
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  sx={{
                    alignItems: { xs: "flex-start", sm: "center" },
                    justifyContent: "space-between",
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{ fontWeight: 700, overflowWrap: "anywhere" }}
                    >
                      {entry.player_display_name}
                      {entry.partner_display_name
                        ? ` / ${entry.partner_display_name}`
                        : ""}
                    </Typography>
                    {entry.seed ? (
                      <Typography color="text.secondary" variant="body2">
                        Seed {entry.seed}
                      </Typography>
                    ) : null}
                  </Stack>
                  <Chip
                    label={statusLabel(entry.status)}
                    size="small"
                    variant="outlined"
                  />
                </Stack>
              ))}
            </Stack>
          </Paper>
        ))
      )}
    </Stack>
  );
}
